import { useState, useCallback } from 'react'
import { Condition, ConditionPrompt } from '../types'
import { supabase } from '../supabaseClient'

export function useConditions(experimentId: string | undefined) {
  const [conditions, setConditions] = useState<Condition[]>([])
  const [conditionPrompts, setConditionPrompts] = useState<ConditionPrompt[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!experimentId) {
      setConditions([])
      setConditionPrompts([])
      return
    }
    setLoading(true)

    const { data: conds } = await supabase
      .from('conditions')
      .select('*')
      .eq('experiment_id', experimentId)
      .order('sort_order')

    const condList = (conds ?? []) as Condition[]
    setConditions(condList)

    if (condList.length > 0) {
      const condIds = condList.map((c) => c.id)
      const { data: cps } = await supabase.from('condition_prompts').select('*').in('condition_id', condIds)
      setConditionPrompts((cps ?? []) as ConditionPrompt[])
    } else {
      setConditionPrompts([])
    }

    setLoading(false)
  }, [experimentId])

  const createCondition = useCallback(
    async (name: string, description?: string): Promise<Condition | null> => {
      if (!experimentId) return null
      const { data, error } = await supabase
        .from('conditions')
        .insert({
          experiment_id: experimentId,
          name,
          description: description ?? null,
          sort_order: conditions.length,
        })
        .select()
        .single()

      if (error) {
        console.error('createCondition error:', error)
        return null
      }
      await load()
      return data as Condition
    },
    [experimentId, conditions.length, load],
  )

  const deleteCondition = useCallback(
    async (id: string) => {
      await supabase.from('conditions').delete().eq('id', id)
      await load()
    },
    [load],
  )

  const updateCondition = useCallback(
    async (id: string, patch: Partial<Pick<Condition, 'name' | 'description'>>) => {
      await supabase.from('conditions').update(patch).eq('id', id)
      await load()
    },
    [load],
  )

  const setPromptForCondition = useCallback(
    async (conditionId: string, passId: string, slot: string, promptId: string) => {
      // Upsert: delete existing then insert
      await supabase
        .from('condition_prompts')
        .delete()
        .eq('condition_id', conditionId)
        .eq('pass_id', passId)
        .eq('slot', slot)

      await supabase.from('condition_prompts').insert({
        condition_id: conditionId,
        pass_id: passId,
        slot,
        prompt_id: promptId,
      })

      await load()
    },
    [load],
  )

  const getPromptsForCondition = useCallback(
    (conditionId: string): ConditionPrompt[] => {
      return conditionPrompts.filter((cp) => cp.condition_id === conditionId)
    },
    [conditionPrompts],
  )

  return {
    conditions,
    conditionPrompts,
    loading,
    load,
    createCondition,
    deleteCondition,
    updateCondition,
    setPromptForCondition,
    getPromptsForCondition,
  }
}
